import { z } from 'zod'
import { addDays, formatFullDate, type MarketMoneyRange } from '@/lib/market-money'

const presetIdSchema = z.enum(['this_month', 'last_month', 'last_30_days', 'quarter', 'year_to_date', 'all_time'])

export type PeriodPresetId = z.infer<typeof presetIdSchema>
export const PERIOD_PRESET_IDS: readonly PeriodPresetId[] = presetIdSchema.options

/** The analytics period is always counted by order date. */
export interface AnalyticsPeriod {
  range: MarketMoneyRange
  preset: PeriodPresetId | null
}

/** What the owner chose: a preset is re-resolved against today, only a drawn range keeps its dates. */
export type PeriodSelection =
  | { kind: 'preset'; preset: PeriodPresetId }
  | { kind: 'range'; range: MarketMoneyRange }

export type PeriodResolution =
  | { status: 'pending'; preset: PeriodPresetId | null }
  | { status: 'ready'; period: AnalyticsPeriod; notice: string | null }

export const PERIOD_PRESET_LABELS: Record<PeriodPresetId, string> = {
  this_month: 'Этот месяц',
  last_month: 'Прошлый месяц',
  last_30_days: 'Последние 30 дней',
  quarter: 'Квартал',
  year_to_date: 'С начала года',
  all_time: 'Всё время',
}

export const DEFAULT_PERIOD_PRESET: PeriodPresetId = 'this_month'
export const DEFAULT_PERIOD_SELECTION: PeriodSelection = { kind: 'preset', preset: DEFAULT_PERIOD_PRESET }
export const PERIOD_STORAGE_KEY = 'almaz:analytics-period:v1'

// URL parameter names are part of the shareable deep link and are stable.
const PARAM = { preset: 'period', from: 'from', to: 'to' } as const
// Links shared before the single order-date basis may still carry it; it is dropped, never honoured.
const LEGACY_PARAMS = ['basis'] as const

function isCalendarDate(value: string | null): value is string {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
}

const calendarDateSchema = z.string().refine((value) => isCalendarDate(value))

export function shiftMonth(monthKey: string, delta: number): string {
  const year = Number(monthKey.slice(0, 4))
  const month = Number(monthKey.slice(5, 7)) - 1 + delta
  return new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 7)
}

export function daysInMonth(monthKey: string): number {
  return new Date(Date.UTC(Number(monthKey.slice(0, 4)), Number(monthKey.slice(5, 7)), 0)).getUTCDate()
}

export function presetRange(preset: PeriodPresetId, today: string, firstOrderDate: string | null): MarketMoneyRange | null {
  switch (preset) {
    case 'this_month':
      return { from: `${today.slice(0, 7)}-01`, to: today }
    case 'last_month': {
      const previous = shiftMonth(today.slice(0, 7), -1)
      return { from: `${previous}-01`, to: `${previous}-${String(daysInMonth(previous)).padStart(2, '0')}` }
    }
    case 'last_30_days':
      return { from: addDays(today, -29), to: today }
    case 'quarter': {
      const quarterStartMonth = Math.floor((Number(today.slice(5, 7)) - 1) / 3) * 3 + 1
      return { from: `${today.slice(0, 4)}-${String(quarterStartMonth).padStart(2, '0')}-01`, to: today }
    }
    case 'year_to_date':
      return { from: `${today.slice(0, 4)}-01-01`, to: today }
    case 'all_time':
      return firstOrderDate ? { from: firstOrderDate, to: today } : null
  }
}

export function parsePeriodUrl(params: URLSearchParams): PeriodSelection | null {
  const preset = presetIdSchema.safeParse(params.get(PARAM.preset))
  if (preset.success) return { kind: 'preset', preset: preset.data }
  const from = params.get(PARAM.from)
  const to = params.get(PARAM.to)
  if (isCalendarDate(from) && isCalendarDate(to) && from <= to) return { kind: 'range', range: { from, to } }
  return null
}

export function serializePeriodUrl(selection: PeriodSelection, current: URLSearchParams = new URLSearchParams()): URLSearchParams {
  const params = new URLSearchParams(current)
  for (const name of [...Object.values(PARAM), ...LEGACY_PARAMS]) params.delete(name)
  if (selection.kind === 'preset') {
    params.set(PARAM.preset, selection.preset)
  } else {
    params.set(PARAM.from, selection.range.from)
    params.set(PARAM.to, selection.range.to)
  }
  return params
}

const storedPeriodSchema = z.discriminatedUnion('kind', [
  z.object({ version: z.literal(1), kind: z.literal('preset'), preset: presetIdSchema }),
  z.object({ version: z.literal(1), kind: z.literal('range'), from: calendarDateSchema, to: calendarDateSchema }),
])

export function parseStoredPeriod(raw: string | null): PeriodSelection | null {
  if (!raw) return null
  let value: unknown
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  const parsed = storedPeriodSchema.safeParse(value)
  if (!parsed.success) return null
  const stored = parsed.data
  if (stored.kind === 'preset') return { kind: 'preset', preset: stored.preset }
  return stored.from <= stored.to ? { kind: 'range', range: { from: stored.from, to: stored.to } } : null
}

export function serializeStoredPeriod(selection: PeriodSelection): string {
  return JSON.stringify(selection.kind === 'preset'
    ? { version: 1, kind: 'preset', preset: selection.preset }
    : { version: 1, kind: 'range', from: selection.range.from, to: selection.range.to })
}

export function browserStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export function readStoredPeriod(storage: Pick<Storage, 'getItem'> | null): PeriodSelection | null {
  if (!storage) return null
  try {
    return parseStoredPeriod(storage.getItem(PERIOD_STORAGE_KEY))
  } catch {
    return null
  }
}

export function writeStoredPeriod(storage: Pick<Storage, 'setItem'> | null, selection: PeriodSelection): void {
  if (!storage) return
  try {
    storage.setItem(PERIOD_STORAGE_KEY, serializeStoredPeriod(selection))
  } catch {
    // Remembering the period is a convenience; a blocked or full storage must not break the page.
  }
}

export function resolveInitialSelection(fromUrl: PeriodSelection | null, fromStorage: PeriodSelection | null): { selection: PeriodSelection; source: 'url' | 'storage' | 'default' } {
  // URL wins; storage fills in only when the URL carries no period; the default (this month) applies when both are silent.
  if (fromUrl) return { selection: fromUrl, source: 'url' }
  if (fromStorage) return { selection: fromStorage, source: 'storage' }
  return { selection: DEFAULT_PERIOD_SELECTION, source: 'default' }
}

export function selectionFromPeriod(period: AnalyticsPeriod): PeriodSelection {
  return period.preset ? { kind: 'preset', preset: period.preset } : { kind: 'range', range: period.range }
}

export interface PeriodResolutionContext {
  today: string
  firstOrderDate: string | null
  /** True once the request that carries the first order date has finished, successfully or not. */
  firstOrderDateSettled: boolean
}

function defaultPeriod(context: PeriodResolutionContext, notice: string): PeriodResolution {
  const range = presetRange(DEFAULT_PERIOD_PRESET, context.today, context.firstOrderDate) ?? { from: context.today, to: context.today }
  return { status: 'ready', period: { range, preset: DEFAULT_PERIOD_PRESET }, notice }
}

export function resolveSelection(selection: PeriodSelection, context: PeriodResolutionContext): PeriodResolution {
  const { firstOrderDate } = context
  if (selection.kind === 'preset') {
    if (selection.preset === 'all_time' && !context.firstOrderDateSettled) return { status: 'pending', preset: selection.preset }
    const range = presetRange(selection.preset, context.today, firstOrderDate)
    if (range) return { status: 'ready', period: { range, preset: selection.preset }, notice: null }
    return defaultPeriod(context, `Период «${PERIOD_PRESET_LABELS[selection.preset]}» сейчас недоступен — показан период по умолчанию`)
  }
  if (!context.firstOrderDateSettled) return { status: 'pending', preset: null }
  const { from, to } = selection.range
  if (firstOrderDate === null || from >= firstOrderDate) {
    return { status: 'ready', period: { range: { from, to }, preset: null }, notice: null }
  }
  if (firstOrderDate > to) {
    return defaultPeriod(context, `Выбранный период раньше первых заказов (${formatFullDate(firstOrderDate)}) — показан период по умолчанию`)
  }
  return {
    status: 'ready',
    period: { range: { from: firstOrderDate, to }, preset: null },
    notice: `Период начинается с ${formatFullDate(firstOrderDate)} — раньше заказов нет`,
  }
}
